/**
 * @fileoverview Overlay de Transmisión Cintillo OBS (/abcdef/overlays/playlist) para "Arma la Playlist".
 * 
 * Diseñado específicamente para producción broadcast en OBS / vMix / TriCaster con fondo transparente,
 * renderizado ultra fluido a 60 FPS acelerado por GPU y sincronización en tiempo real.
 * 
 * Estructura y diseño basados en los overlays de Convivencia en la Zona (QuizzB9Zone - 2 QRs)
 * y Rooster (PluckingRooster - 1 QR).
 * 
 * @module components/Overlays/Playlist
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetSettings, GetPublicConfig } from '../../utilities/DL/Admin/Overlays/Playlist';
import { PlaylistWS } from '../../utilities/WS/Admin/PlaylistWS';
import { Playlist as PlaylistOverlayView } from './Overlays/Playlist';

/**
 * COMPONENTE: PLAYLIST OVERLAY (PARENT CONTAINER)
 * ------------------------------------------------------------------
 * Este componente es el "Cerebro" del Overlay.
 * 1. Establece la conexión WebSocket / SignalR para "Arma la Playlist".
 * 2. Carga la configuración inicial desde la API (offsetY, imágenes, qrCount, etc.).
 * 3. Escucha actualizaciones en tiempo real y gestiona el ciclo de visibilidad (flowState).
 * 4. Pasa los datos al componente visual <PlaylistOverlayView />.
 * ------------------------------------------------------------------
 * 
 * @component
 * @param {Object} props
 * @param {string} [props.roomId=''] - Identificador opcional de la sala.
 */
export function Playlist({ roomId: propRoomId = '' }) {
    // 1. Obtener roomId opcionalmente de query params o props
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const roomId = propRoomId || searchParams.get('roomId') || searchParams.get('room') || '';

    // 2. Estados reactivos de configuración visual
    const [offsetY, setOffsetY] = useState(0);
    const [bannerImageMdUrl, setBannerImageMdUrl] = useState('');
    const [bannerImageLgUrl, setBannerImageLgUrl] = useState('');
    const [qrCount, setQrCount] = useState(1);
    const [qrCenterLogoUrl, setQrCenterLogoUrl] = useState('');
    const [connectionStatus, setConnectionStatus] = useState('idle');

    // 3. Control del flujo de visibilidad ('idle' | 'entering' | 'visible' | 'fadingOut')
    const [flowState, setFlowState] = useState('idle');

    // 4. Forzar fondo transparente total para integración OBS Browser Source
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const rootElement = document.getElementById('root');
        const originalRootBg = rootElement ? rootElement.style.backgroundColor : '';

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            if (rootElement) {
                rootElement.style.backgroundColor = originalRootBg;
            }
        };
    }, []);

    // 5. Control de transiciones suaves de animación (entering -> visible, fadingOut -> idle)
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            timeout = setTimeout(() => setFlowState('visible'), 500);
        } else if (flowState === 'fadingOut') {
            timeout = setTimeout(() => {
                setFlowState('idle');
            }, 500);
        }
        return () => clearTimeout(timeout);
    }, [flowState]);

    const handleOverlayVisibilityChange = useCallback((visible) => {
        if (visible) {
            setFlowState((prev) => {
                if (prev === 'idle' || prev === 'fadingOut') {
                    return 'entering';
                }
                return prev;
            });
        } else {
            setFlowState((prev) => {
                if (prev !== 'idle' && prev !== 'fadingOut') {
                    return 'fadingOut';
                }
                return prev;
            });
        }
    }, []);

    // 6. Aplicar datos de configuración recibidos
    const applyConfigData = useCallback((data) => {
        if (!data) return;

        if (data.overlayOffsetY !== undefined) {
            setOffsetY(Number(data.overlayOffsetY) || 0);
        } else if (data.offsetY !== undefined) {
            setOffsetY(Number(data.offsetY) || 0);
        }

        const mdImg = data.overlayBackground1536Url || data.overlayBackgroundMediumUrl || data.bannerImageMdUrl;
        if (mdImg !== undefined) {
            setBannerImageMdUrl(mdImg || '');
        }

        const lgImg = data.overlayBackground1920Url || data.overlayBackgroundFullHdUrl || data.bannerImageLgUrl;
        if (lgImg !== undefined) {
            setBannerImageLgUrl(lgImg || '');
        }

        if (data.qrCount !== undefined) {
            setQrCount(Number(data.qrCount) || 1);
        }

        if (data.qrCenterLogoUrl !== undefined) {
            setQrCenterLogoUrl(data.qrCenterLogoUrl || '');
        }

        if (data.isOverlayVisible !== undefined) {
            const isVisible = Boolean(data.isOverlayVisible);
            handleOverlayVisibilityChange(isVisible);
        }
    }, [handleOverlayVisibilityChange]);

    // 7. Carga inicial de datos y conexión a WebSocket / SignalR
    useEffect(() => {
        const init = async () => {
            try {
                // Intentar cargar configuración completa o pública
                let res = await GetSettings(roomId);
                if (!res || !res.success || !res.data) {
                    res = await GetPublicConfig({ roomId });
                }

                if (res && res.success && res.data) {
                    applyConfigData(res.data);
                    const isVisible = res.data.isOverlayVisible !== false;
                    setFlowState(isVisible ? 'visible' : 'idle');
                }
            } catch (err) {
                console.warn('[PlaylistOverlay] No se pudo cargar configuración inicial:', err);
            }

            // Manejador de eventos en tiempo real
            const wsHandlers = {
                settings_updated: (payload) => {
                    applyConfigData(payload);
                },
                config: (payload) => {
                    applyConfigData(payload);
                },
                state: (payload) => {
                    applyConfigData(payload);
                },
                playlist_settings_updated: (payload) => {
                    applyConfigData(payload);
                },
                onMessage: (envelope) => {
                    if (!envelope) return;
                    const type = (envelope.type || '').toLowerCase();
                    const data = envelope.payload || envelope.data || envelope;
                    if (
                        type === 'settings_updated' ||
                        type === 'playlist:settings_updated' ||
                        type === 'config' ||
                        type === 'state'
                    ) {
                        applyConfigData(data);
                    }
                }
            };

            PlaylistWS.connect(roomId, wsHandlers, (status) => {
                setConnectionStatus(status);
            });
        };

        init();

        return () => {
            PlaylistWS.disconnect();
        };
    }, [roomId, applyConfigData]);

    return (
        <>
            <PlaylistOverlayView
                flowState={flowState}
                offsetY={offsetY}
                bannerImageMdUrl={bannerImageMdUrl}
                bannerImageLgUrl={bannerImageLgUrl}
                qrCount={qrCount}
                qrCenterLogoUrl={qrCenterLogoUrl}
                roomId={roomId}
            />

            {connectionStatus === 'error' && (
                <div style={{
                    position: 'fixed',
                    bottom: 5,
                    right: 5,
                    background: 'red',
                    color: 'white',
                    padding: '3px 8px',
                    fontSize: '9px',
                    borderRadius: '4px',
                    opacity: 0.8,
                    zIndex: 99999
                }}>
                    D
                </div>
            )}
        </>
    );
}

Playlist.propTypes = {
    roomId: PropTypes.string
};

export default Playlist;
